import { useEffect, useRef, useState } from 'react';

interface UseSliderProps {
	slidesCount: number;
	autoPlayInterval?: number;
}

export const useSlider = ({ slidesCount, autoPlayInterval }: UseSliderProps) => {
	const slidesContainerRef = useRef(null);
	const [userHasScrolled, setUserHasScrolled] = useState(true);
	const [currentSlide, setCurrentSlide] = useState(0);

	const handleSlideChange = (index: number) => {
		setUserHasScrolled(true);
		setCurrentSlide(index);
		const slidesRef = slidesContainerRef?.current as unknown as HTMLElement;
		const slideWidth = slidesRef?.children?.[0]?.getBoundingClientRect().width;
		slidesRef.scrollLeft = slideWidth * index;
	};

	const handleScroll = () => {
		const slidesRef = slidesContainerRef?.current as unknown as HTMLElement;
		const slideWidth = slidesRef?.children?.[0]?.getBoundingClientRect().width;
		const newCurrentSlide = Math.round(slidesRef.scrollLeft / slideWidth);
		setCurrentSlide(newCurrentSlide);
		setUserHasScrolled(true);
	};

	useEffect(() => {
		const slidesRef = slidesContainerRef?.current as unknown as HTMLElement;
		slidesRef?.addEventListener('scroll', handleScroll);
		return () => slidesRef?.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		if (autoPlayInterval === undefined) return;

		const timer = setInterval(() => {
			if (!userHasScrolled && slidesCount) {
				setUserHasScrolled(false);
			} else {
				handleSlideChange((currentSlide + 1) % slidesCount);
			}
		}, autoPlayInterval);

		return () => clearInterval(timer);
	}, [userHasScrolled, currentSlide, slidesCount, autoPlayInterval]);

	useEffect(() => {
		setCurrentSlide(0);
		setUserHasScrolled(true);
		const slidesRef = slidesContainerRef?.current as unknown as HTMLElement;
		if (slidesRef) {
			slidesRef.scrollLeft = 0;
		}
	}, [slidesCount]);

	return {
		currentSlide,
		slidesContainerRef,
		handleSlideChange
	};
};
